import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, Mail, MailCheck, ShieldCheck } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { SunkevLogo } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-ink outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-navy focus:ring-4 focus:ring-navy/10";

const Login = () => {
  const { login, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [step, setStep] = useState("credentials");
  const [mode, setMode] = useState("password");
  const [linkSent, setLinkSent] = useState(false);

  const sendLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/magic-link`, {
        email: form.email,
      });
    } catch {
      /* response identical either way */
    } finally {
      setBusy(false);
      setLinkSent(true);
    }
  };
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(form.email, form.password);
      if (u.otp_required) {
        setStep("otp");
        toast.success("Verification code sent to your email");
      } else {
        toast.success(`Welcome back, ${u.name || u.email}`);
        navigate("/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await verifyOtp(form.email, code);
      toast.success(`Welcome back, ${u.name || u.email}`);
      navigate(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex min-h-screen items-center justify-center bg-mist px-5 pb-16 pt-28">
      <Reveal className="w-full max-w-md">
        <div className="rounded-3xl border border-gray-100 bg-white p-10 shadow-xl shadow-navy/5">
          <div className="flex justify-center">
            <SunkevLogo compact />
          </div>

          {step === "credentials" ? (
            <>
              <h1 className="mt-8 text-center font-heading text-2xl font-bold text-ink">
                Administrator Sign In
              </h1>
              <p className="mt-2 text-center text-sm text-fog">
                Restricted to authorised Sunkev Enterprises administrators
              </p>
              <div className="mt-6 grid grid-cols-2 gap-1 rounded-full bg-mist p-1" data-testid="login-mode-tabs">
                {[
                  { key: "password", label: "Password" },
                  { key: "link", label: "Email Link" },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      setMode(t.key);
                      setError("");
                    }}
                    data-testid={`mode-${t.key}`}
                    className={`rounded-full py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                      mode === t.key ? "bg-navy text-white" : "text-fog hover:text-navy"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {mode === "link" ? (
                linkSent ? (
                  <div className="mt-8 text-center" data-testid="magic-sent">
                    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                      <MailCheck className="h-6 w-6 text-emerald-600" />
                    </span>
                    <p className="mt-4 text-sm leading-relaxed text-fog">
                      If this email is registered, a secure sign-in link is on its way. It
                      works once and expires in 15 minutes.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={sendLink} className="mt-8 space-y-5" data-testid="magic-form">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        required
                        placeholder="Admin email address"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        data-testid="magic-email-input"
                        className={inputCls}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="magic-send-button"
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 disabled:opacity-60"
                    >
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email Me a Sign-In Link"}
                    </button>
                  </form>
                )
              ) : (
              <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    required
                    placeholder="Email address"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    data-testid="login-email-input"
                    className={inputCls}
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    required
                    placeholder="Password"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    data-testid="login-password-input"
                    className={inputCls}
                  />
                </div>
                {error && (
                  <p data-testid="login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-lg hover:shadow-navy/25 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
                </button>
                <p className="text-center">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-sm font-medium text-fog transition-colors duration-300 hover:text-navy"
                  >
                    Forgot password?
                  </Link>
                </p>
              </form>
              )}
            </>
          ) : (
            <>
              <div className="mt-8 flex justify-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-navy">
                  <ShieldCheck className="h-6 w-6 text-gold" />
                </span>
              </div>
              <h1 className="mt-6 text-center font-heading text-2xl font-bold text-ink">
                Two-Factor Verification
              </h1>
              <p className="mt-2 text-center text-sm text-fog">
                Enter the 6-digit code sent to{" "}
                <span className="font-semibold text-ink">{form.email}</span>
              </p>
              <form onSubmit={onVerify} className="mt-8 space-y-5" data-testid="otp-form">
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••••"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="otp-code-input"
                  className="w-full rounded-xl border border-gray-200 bg-white py-3.5 text-center font-heading text-2xl font-bold tracking-[0.5em] text-ink outline-none transition-all duration-300 placeholder:text-gray-300 focus:border-navy focus:ring-4 focus:ring-navy/10"
                />
                {error && (
                  <p data-testid="otp-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  data-testid="otp-verify-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 font-heading text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-navy-800 hover:shadow-lg hover:shadow-navy/25 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign In"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep("credentials");
                    setCode("");
                    setError("");
                  }}
                  data-testid="otp-back-button"
                  className="w-full text-center text-sm font-medium text-fog transition-colors duration-300 hover:text-navy"
                >
                  Use a different account
                </button>
              </form>
            </>
          )}

          <p className="mt-8 text-center text-xs leading-relaxed text-fog">
            Accounts are created and managed by Sunkev Enterprises.
            <br />
            Public sign-up is disabled.
          </p>
        </div>
      </Reveal>
    </section>
  );
};

export default Login;
